import { DashboardWidgetId } from "@/lib/types/dashboard-widgets";

/**
 * Which studio dashboard lists to load.
 * A list is requested only when a visible widget or the Class soon chip needs it.
 * Daily workflow enrollment chips use their own endpoints and are not planned here.
 */
export type StudioDashboardFetchPlan = {
  /** All student records, count only. Enrolled Students subtitle. */
  studentTotals: boolean;
  /** Active students who have a class, count only. Enrolled Students value. */
  enrolledStudents: boolean;
  /** Class search page used for teacher load and as the id source for class details. */
  classCatalog: boolean;
  /** Class-by-id details: schedule, location, and room. */
  classDetails: boolean;
  /** Total classes count when the class catalog itself is not loaded. */
  classTotalCount: boolean;
  /** Active classes count for the Total Classes subtitle. */
  classActiveCount: boolean;
  /**
   * Full teacher list. Teacher Load needs every teacher, including those with
   * no classes. Total Teachers needs it for the "with classes" subtitle.
   */
  teacherCatalog: boolean;
  seasonCatalog: boolean;
  seasonActiveCount: boolean;
  locationCatalog: boolean;
  locationActiveCount: boolean;
  classroomCatalog: boolean;
  classroomActiveCount: boolean;
  policyCatalog: boolean;
  policyActiveCount: boolean;
  /** Full enrollment scroll for widgets that count or list enrollment rows. */
  enrollmentCatalog: boolean;
  attendance: boolean;
  /** Low-enrollment threshold and teacher-load bucket settings. */
  operationsSettings: boolean;
};

const CLASS_DETAIL_WIDGETS: readonly DashboardWidgetId[] = [
  DashboardWidgetId.UpcomingClasses,
  DashboardWidgetId.LowEnrollmentClasses,
  DashboardWidgetId.LocationUsage,
  DashboardWidgetId.Locations,
  DashboardWidgetId.ClassroomUtilization,
  DashboardWidgetId.Classrooms,
];

export function buildStudioDashboardFetchPlan(input: {
  kpiWidgets: readonly DashboardWidgetId[];
  regularWidgets: readonly DashboardWidgetId[];
  classStartingSoon: boolean;
}): StudioDashboardFetchPlan {
  const kpi = new Set(input.kpiWidgets);
  const regular = new Set(input.regularWidgets);
  const hasRegular = (id: DashboardWidgetId) => regular.has(id);
  const hasKpi = (id: DashboardWidgetId) => kpi.has(id);

  const classDetails =
    input.classStartingSoon || CLASS_DETAIL_WIDGETS.some(hasRegular);
  const classCatalog =
    classDetails || hasRegular(DashboardWidgetId.TeacherLoad);
  const teacherLoad = hasRegular(DashboardWidgetId.TeacherLoad);
  const totalTeachers = hasKpi(DashboardWidgetId.TotalTeachers);

  return {
    studentTotals: hasKpi(DashboardWidgetId.TotalStudents),
    enrolledStudents: hasKpi(DashboardWidgetId.TotalStudents),
    classCatalog,
    classDetails,
    classTotalCount: hasKpi(DashboardWidgetId.TotalClasses) && !classCatalog,
    classActiveCount: hasKpi(DashboardWidgetId.TotalClasses),
    teacherCatalog: teacherLoad || totalTeachers,
    seasonCatalog: hasRegular(DashboardWidgetId.ActiveSeasons),
    seasonActiveCount: hasKpi(DashboardWidgetId.TotalSeasons),
    locationCatalog:
      hasRegular(DashboardWidgetId.Locations) ||
      hasRegular(DashboardWidgetId.LocationUsage) ||
      hasRegular(DashboardWidgetId.Classrooms),
    locationActiveCount: hasKpi(DashboardWidgetId.TotalLocations),
    classroomCatalog:
      hasRegular(DashboardWidgetId.Classrooms) ||
      hasRegular(DashboardWidgetId.ClassroomUtilization),
    classroomActiveCount: hasKpi(DashboardWidgetId.TotalClassrooms),
    policyCatalog: hasRegular(DashboardWidgetId.Policies),
    policyActiveCount: hasKpi(DashboardWidgetId.TotalPolicies),
    enrollmentCatalog:
      hasRegular(DashboardWidgetId.LowEnrollmentClasses) ||
      hasRegular(DashboardWidgetId.StudentsProgress) ||
      hasRegular(DashboardWidgetId.ClassroomUtilization) ||
      hasRegular(DashboardWidgetId.Classrooms) ||
      hasRegular(DashboardWidgetId.EnrollmentsCalendar),
    attendance: hasRegular(DashboardWidgetId.Attendance),
    operationsSettings:
      teacherLoad || hasRegular(DashboardWidgetId.LowEnrollmentClasses),
  };
}
